/**
 * EL CARGO DEL BANCO: lo que se queda el banco de un cobro, anotado como un
 * egreso propio en vez de restado del ingreso.
 *
 * La regla en una frase (spec 2026-09-22-cuotas-cargos-del-banco): un cobro por un método con
 * tarifa escribe DOS filas —— el ingreso y su cargo. El cliente paga 100, el
 * negocio cobra 100, y el negocio anota aparte que el banco se quedó 3,50. No
 * es un recargo al cliente, no es una línea del ticket y no es un descuento
 * sobre el monto.
 *
 * ESTÁ AQUÍ Y NO EN EL BACKEND porque el porcentaje se escribe en una pantalla
 * y se aplica en el servidor: las dos mitades tienen que redondear igual o el
 * importe que se enseñó al configurar no es el que aparece en el Libro.
 */
import { PAYMENT_METHOD_LABELS } from "./models.js";
/**
 * LA TARIFA VIAJA EN PUNTOS BÁSICOS, ENTEROS: 3,5 % es `350`.
 *
 * Misma razón que el dinero en centavos, escrita entera en `money.ts`: un
 * porcentaje en coma flotante se redondea distinto en Postgres, en Node y en
 * Hermes, y el resultado es un importe que no cuadra con el que se enseñó en
 * pantalla. El único sitio donde existe una coma es la cadena que lee una
 * persona.
 */
const FEE_RATE_SCALE = 10_000;
/**
 * EL 99 %, y el tope se escribe aquí una sola vez —— lo leen el zod de la ruta,
 * `parseRateToBp` y `sanitizeRateText`, que es lo que impide que se pueda
 * TECLEAR un número que luego se rechaza.
 *
 * NO ES EL 100. Un banco que se quedara con el cobro entero no es una tarifa,
 * es un dato mal escrito, y el sitio donde se atrapa es el teclado.
 *
 * El `check` de la 0056 admite hasta 10000 y eso está bien: la base impide lo
 * que no es un por ciento, la regla vive aquí.
 */
export const MAX_FEE_RATE_BP = 99 * 100;
/**
 * LA CATEGORÍA DONDE CAEN LOS CARGOS, y está en todas las listas del seed
 * (`business-type.ts`) y en el backfill de la 0056.
 *
 * SE USA PARA ESCRIBIR LA FILA, NUNCA PARA ENCONTRARLA. El vínculo vivo es
 * `business.fee_category_id`, un id: el dueño puede renombrar la categoría sin
 * que los cargos dejen de caer en ella.
 */
export const BANK_FEE_CATEGORY = "Cargos del banco";
/**
 * Lo que el banco se queda de `amountCents` a una tarifa de `rateBp`, al
 * centavo y medio hacia arriba.
 *
 * DEVUELVE CERO EN VEZ DE LANZAR cuando no hay nada que cobrar —— tarifa cero,
 * monto cero, o un cargo que no llega al centavo. Quien llama ya tiene que
 * mirar el cero de todas formas, porque un egreso de 0,00 no se escribe (spec
 * §3): un error aquí obligaría a envolver la misma pregunta en un try.
 */
export function bankFeeCents(amountCents, rateBp) {
    if (amountCents <= 0 || rateBp <= 0)
        return 0;
    return Math.round((amountCents * rateBp) / FEE_RATE_SCALE);
}
/**
 * Lo que el cargo dice en el Libro: «Cargo por Yappy».
 *
 * Leído de `PAYMENT_METHOD_LABELS` y nunca escrito por país —— la misma
 * agnosticidad que la pantalla: en Perú sale «Cargo por
 * Yape» sin que nadie escriba un `if`.
 *
 * «CARGO» Y NUNCA «COMISIÓN». En el lavadero comisión es lo que el CLIENTE deja
 * de propina encima del precio y el lavador se queda en mano —— ver
 * `washes.ts` ——, o sea casi lo contrario de esto. Una palabra que ya tiene
 * dueño en una vertical no puede nombrar algo del núcleo.
 */
export function feeConceptFor(method) {
    return `Cargo por ${PAYMENT_METHOD_LABELS[method]}`;
}
/**
 * EL PORCENTAJE COMO LO LEE Y LO ESCRIBE UNA PERSONA.
 *
 * Son las dos mitades de un campo, y viven aquí en vez de en cada cliente por
 * la misma razón que `bankFeeCents`: la web y el teléfono tienen que aceptar y
 * mostrar «3,5» exactamente igual, y el separador decimal es el del PAÍS —— «3,5»
 * en Venezuela, «3.5» en Panamá —— como ya hace todo importe.
 *
 * DOS DECIMALES Y NO LOS DEL PAÍS: un porcentaje no es dinero. En Colombia el
 * peso no tiene decimales y una tarifa de 3,5 % sí, así que `Country.decimals`
 * no manda aquí.
 */
const RATE_DECIMALS = 2;
/** `350` → `"3.5"`, `500` → `"5"`. Sin ceros de adorno: nadie escribe «5,00 %». */
export function rateToInput(rateBp, decimalSeparator = ".") {
    const text = (rateBp / 100).toFixed(RATE_DECIMALS).replace(/\.?0+$/, "");
    return text.replace(".", decimalSeparator);
}
/**
 * Lo que alguien tecleó, en puntos básicos, o null si no es un porcentaje que
 * cobre algo.
 *
 * NULL PARA EL CERO, a propósito: apagar un método es quitarlo de la lista, no
 * escribirle un cero. Dos formas de decir «no cobra» serían dos formas de que
 * una consulta se olvide de una.
 */
export function parseRateToBp(input, decimalSeparator = ".") {
    // El signo se caza ANTES de limpiar, igual que en `parseAmountToCents` y por
    // lo mismo: el barrido de caracteres lo borraría y «-3» pasaría por un 3 %.
    if (input.trim().startsWith("-"))
        return null;
    const cleaned = input.trim().replace(decimalSeparator, ".").replace(/[^\d.]/g, "");
    if (cleaned === "" || (cleaned.match(/\./g) ?? []).length > 1)
        return null;
    const value = Number(cleaned);
    if (!Number.isFinite(value) || value <= 0)
        return null;
    const bp = Math.round(value * 100);
    if (bp <= 0 || bp > MAX_FEE_RATE_BP)
        return null;
    return bp;
}
/**
 * LO QUE EL CAMPO DEJA ESCRIBIR, tecla a tecla —— hermana de
 * `sanitizeAmountText` en `money.ts`, y por la misma razón que existe aquélla:
 * un campo que ACEPTA letras y las limpia al guardar deja a alguien mirando
 * «edgaegsasdgadg» dentro de una caja que pide un porcentaje. Un número no se corrige, no se teclea.
 *
 * DÍGITOS Y UN SOLO SEPARADOR. Lo que venga detrás del primero se pega al
 * primero —— eso es lo que impide que «3....5» críe un separador por pulsación
 * —— y se corta a dos decimales. Se devuelve con el separador del PAÍS, así que
 * un campo venezolano enseña «3,5» mientras se escribe.
 */
export function sanitizeRateText(text, decimalSeparator = ".") {
    const cleaned = text.replace(decimalSeparator, ".").replace(/[^\d.]/g, "");
    // DOS DÍGITOS ENTEROS, o sea 99 como mucho —— ver `MAX_FEE_RATE_BP`. Se corta
    // igual que los decimales y por lo mismo: el tope se aplica al TECLEAR, no al
    // guardar, así que nadie escribe un número que la pantalla luego rechaza.
    const [rawWhole = "", ...rest] = cleaned.split(".");
    const whole = rawWhole.slice(0, 2);
    if (rest.length === 0)
        return whole;
    return `${whole}${decimalSeparator}${rest.join("").slice(0, RATE_DECIMALS)}`;
}
